<template>
  <div class="main-container">
    <AnalyticsHeader :selectedDepartments.sync="selectedDepartments" />

    <AnalyticsOverview
      :selectedDepartments="selectedDepartments"
      :resolvedCount="filteredResolvedCount"
      :inProgressCount="filteredInProgressCount"
      :abandonedCount="filteredAbandonedCount"
      :inQueueCount="filteredQueueChats.length"
      :averageWaitTime="filteredAverageWaitTimeString"
    />

    <div class="monitor-container">
      <AgentMonitor :selectedDepartments="selectedDepartments" />
      <ChatQueue
        :queueChats="filteredQueueChats"
        :selectedDepartments="selectedDepartments"
        :averageWaitTime="filteredAverageWaitTime"
      />
    </div>
  </div>
</template>
<script>
import _ from 'lodash';
import { gql } from '@apollo/client/core';
import moment from 'moment-timezone';
import AnalyticsHeader from './AnalyticsHeader.vue';
import AnalyticsOverview from './AnalyticsOverview.vue';
import AgentMonitor from './AgentMonitor.vue';
import ChatQueue from './ChatQueue.vue';
import { liveAnalyticsMixin } from '@/mixins/liveAnalytics';

// Chat type constants
const CHAT_TYPES = {
  RESOLVED: 'resolved',
  ABANDONED: 'abandoned',
  IN_PROGRESS: 'inProgress',
  QUEUE: 'queue',
};

// Map chat types to data property names
const CHAT_ARRAY_MAP = {
  [CHAT_TYPES.RESOLVED]: 'resolvedChats',
  [CHAT_TYPES.ABANDONED]: 'abandonedChats',
  [CHAT_TYPES.IN_PROGRESS]: 'inProgressChats',
  [CHAT_TYPES.QUEUE]: 'queueChats',
};

export default {
  name: 'LiveAnalytics',
  mixins: [liveAnalyticsMixin],
  components: {
    AnalyticsHeader,
    AnalyticsOverview,
    AgentMonitor,
    ChatQueue,
  },
  data() {
    return {
      resolvedChats: [],
      abandonedChats: [],
      inProgressChats: [],
      queueChats: [],
      departmentIndex: {},
      selectedDepartments: [],
      lastResetDate: null,
      now: moment().local(),
    };
  },
  apollo: {
    $subscribe: {
      livechatResolved: {
        query: gql`subscription { livechatResolved }`,
        result({ data }) {
          const chat = data?.livechatResolved?.chat;
          if (!chat) return;

          const chatId = this.getChatId(chat);
          const hasAgents = chat.agents?.length > 0;
          const department = this.getChatDepartmentId(chat);

          if (hasAgents) {
            this.addChat(CHAT_TYPES.RESOLVED, chat, chatId, department);
            this.removeChat(CHAT_TYPES.IN_PROGRESS, chatId, department);
            this.updateAgentChatTracking('REMOVE', chat.agents, chatId);
          } else {
            this.removeChat(CHAT_TYPES.QUEUE, chatId, department);
            this.addChat(CHAT_TYPES.ABANDONED, chat, chatId, department);
          }
        },
      },
      livechatJoinQueue: {
        query: gql`subscription { livechatJoinQueue }`,
        result({ data }) {
          const chat = data?.livechatJoinQueue;
          if (!chat || chat.resolved) return;

          const chatId = this.getChatId(chat);
          const hasAgents = chat.agents?.length > 0;
          const department = this.getChatDepartmentId(chat);

          if (hasAgents) {
            this.removeChat(CHAT_TYPES.QUEUE, chatId, department);
            this.addChat(CHAT_TYPES.IN_PROGRESS, chat, chatId, department);
            this.updateAgentChatTracking('ADD', chat.agents, chatId);
          } else {
            this.addChat(CHAT_TYPES.QUEUE, chat, chatId, department);
          }
        },
      },
      livechatSessionUpdated: {
        query: gql`subscription { livechatSessionUpdated }`,
        result({ data }) {
          const chat = data?.livechatSessionUpdated;
          if (!chat || !chat.agents?.length || chat.resolved) return;

          const chatId = this.getChatId(chat);
          const department = this.getChatDepartmentId(chat);
          const wasInQueue = this.queueChats.some(
            (c) => this.getChatId(c) === chatId,
          );

          if (wasInQueue) {
            this.removeChat(CHAT_TYPES.QUEUE, chatId, department);
            this.addChat(CHAT_TYPES.IN_PROGRESS, chat, chatId, department);
            this.updateAgentChatTracking('ADD', chat.agents, chatId);
          }
        },
      },
      livechatSessionLeave: {
        query: gql`subscription { livechatSessionLeave }`,
        result({ data }) {
          const { previousChat, previousAgents = [] } = data?.livechatSessionLeave || {};
          if (!previousChat) return;

          const chatId = this.getChatId(previousChat);
          const department = this.getChatDepartmentId(previousChat);
          const hasAgents = previousChat.agents?.length > 0;
          const isResolved = previousChat.resolved;

          // Remove tracking for agents who left
          const leftAgents = previousAgents.filter(
            (agent) => !previousChat.agents?.includes(agent),
          );
          this.updateAgentChatTracking('REMOVE', leftAgents, chatId);

          if (!hasAgents && !isResolved) {
            this.removeChat(CHAT_TYPES.IN_PROGRESS, chatId, department);
            this.addChat(CHAT_TYPES.QUEUE, previousChat, chatId, department);
          }
        },
      },
    },
  },
  computed: {
    liveAnalyticsSettings() {
      return _.get(this.$store.state.modules, 'handover.liveChatLiveAnalytics', {});
    },
    autoResetEnabled() {
      return this.liveAnalyticsSettings?.autoReset === true;
    },
    resetAtTime() {
      return this.liveAnalyticsSettings?.resetAt || '00:00';
    },
    filteredQueueChats() {
      if (!this.selectedDepartments?.length) {
        return this.queueChats;
      }
      return this.queueChats.filter((chat) =>
        this.chatInSelectedDepartments(chat, this.selectedDepartments, this.departmentIndex),
      );
    },
    filteredResolvedCount() {
      return this.getFilteredCount(CHAT_TYPES.RESOLVED, this.resolvedChats);
    },
    filteredInProgressCount() {
      return this.getFilteredCount(CHAT_TYPES.IN_PROGRESS, this.inProgressChats);
    },
    filteredAbandonedCount() {
      return this.getFilteredCount(CHAT_TYPES.ABANDONED, this.abandonedChats);
    },
    filteredAverageWaitTime() {
      if (this.filteredQueueChats.length === 0) return 0;

      const totalWaitMs = this.filteredQueueChats.reduce((sum, chat) => {
        const createdAt = moment(chat.date_created);
        return sum + this.now.diff(createdAt);
      }, 0);

      return totalWaitMs / this.filteredQueueChats.length;
    },
    filteredAverageWaitTimeString() {
      return this.formatDuration(this.filteredAverageWaitTime);
    },
  },
  methods: {
    // Add chat to array and update department index
    addChat(type, chat, chatId, department) {
      const arrName = CHAT_ARRAY_MAP[type];
      const arr = this[arrName];

      if (!arr.some((c) => this.getChatId(c) === chatId)) {
        this[arrName] = [...arr, chat];
      }

      if (department) {
        if (!this.departmentIndex[department]) {
          this.$set(this.departmentIndex, department, {
            [CHAT_TYPES.RESOLVED]: new Set(),
            [CHAT_TYPES.ABANDONED]: new Set(),
            [CHAT_TYPES.IN_PROGRESS]: new Set(),
            [CHAT_TYPES.QUEUE]: new Set(),
          });
        }
        this.departmentIndex[department][type].add(chatId);
      }
    },

    // Remove chat from array and update department index
    removeChat(type, chatId, department) {
      const arrName = CHAT_ARRAY_MAP[type];
      this[arrName] = this[arrName].filter((c) => this.getChatId(c) !== chatId);

      if (department && this.departmentIndex[department]) {
        this.departmentIndex[department][type].delete(chatId);
      }
    },

    // Get filtered count using department index
    getFilteredCount(type, chats) {
      if (!this.selectedDepartments?.length) {
        return chats.length;
      }
      return this.getCountForDepartments(type, this.selectedDepartments, this.departmentIndex);
    },

    // Update agent chat tracking in store
    updateAgentChatTracking(action, agents, chatId) {
      const mutation = action === 'ADD' ? 'ADD_AGENT_CHAT' : 'REMOVE_AGENT_CHAT';
      agents.forEach((agentEmail) => {
        this.$store.commit(`supervisor/${mutation}`, { agentEmail, chatId });
      });
    },

    // Fetch initial data from store
    async fetchInitialData() {
      try {
        await Promise.all([
          this.$store.dispatch('supervisor/FETCH_RESOLVED_CHATS'),
          this.$store.dispatch('supervisor/FETCH_ABANDONED_CHATS'),
          this.$store.dispatch('supervisor/FETCH_IN_QUEUE_CHATS'),
          this.$store.dispatch('supervisor/FETCH_ON_GOING_CHATS'),
          this.$store.dispatch('FETCH_AGENTS'),
          this.$store.dispatch('FETCH_DEPARTMENTS'),
        ]);

        const state = this.$store.state.supervisor;
        this.resolvedChats = [...(state.resolvedChatsArray || [])];
        this.abandonedChats = [...(state.abandonedChatsArray || [])];
        this.inProgressChats = [...(state.onGoingChatsArray || [])];
        this.queueChats = [...(state.inQueueChatsArray || [])];

        this.departmentIndex = this.buildDepartmentIndex({
          [CHAT_TYPES.RESOLVED]: this.resolvedChats,
          [CHAT_TYPES.ABANDONED]: this.abandonedChats,
          [CHAT_TYPES.IN_PROGRESS]: this.inProgressChats,
          [CHAT_TYPES.QUEUE]: this.queueChats,
        });

        this.$store.commit('supervisor/INIT_AGENT_CHATS', {
          chats: this.inProgressChats,
        });
      } catch {
        this.$notify.error({
          title: 'Error',
          message: 'Failed to load analytics data. Please refresh.',
        });
      }
    },

    checkAutoReset() {
      if (!this.autoResetEnabled) return;

      const now = moment().local();
      const currentTime = now.format('HH:mm');
      const today = now.format('YYYY-MM-DD');

      // Check if current time matches reset time and we haven't reset today
      if (currentTime === this.resetAtTime && this.lastResetDate !== today) {
        this.resetAnalytics();
        this.lastResetDate = today;
      }
    },
    resetAnalytics() {
      this.resolvedChats = [];
      this.abandonedChats = [];
      this.inProgressChats = [];
      this.queueChats = [];
      this.departmentIndex = {};
      this.$store.commit('supervisor/CLEAR_AGENT_CHATS');
      this.fetchInitialData();
    },
  },
  mounted() {
    this._timeInterval = setInterval(() => {
      this.now = moment().local();
      this.checkAutoReset();
    }, 1000);

    this.fetchInitialData();
  },
  beforeDestroy() {
    if (this._timeInterval) {
      clearInterval(this._timeInterval);
    }
    this.$store.commit('SET_NAVBAR_VISIBLE', true);
    this.$store.commit('supervisor/SET_LOCK_ON_LIVE_ANALYTICS', false);
  },
};
</script>
<style lang="scss" scoped>
@import '../../../assets/scss/colors.scss';

.main-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: $color-light;
  gap: 2rem;
}

.monitor-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  padding: 0 1rem;

  @media only screen and (max-width: 850px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem 0rem;
  }
}
</style>
